import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { APPLICATION_NAME, ENTITY_TYPE } from '@app/shared/utils/app.utils';
import {
    EntityService,
    IndexedSessionEntities,
    SessionService,
    UserEntity,
    sortByProperty,
} from '@le_cedre/portal-lib';
import Cookie from 'js-cookie';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@env/environment';

@Component({
    selector: 'app-entity-selector-page',
    template: `
        <div class="entity-selector">
            <div class="es-header">
                <span class="structure-icon">
                    <img class="es-icon filter-white" src="assets/structure.svg" />
                </span>
                <span [innerHTML]="title | translate"></span>
            </div>

            <div *ngIf="entities?.length === 0; then thenBlock; else elseBlock"></div>

            <ng-template #thenBlock>
                <div class="es-empty-selection">
                    <span [innerHTML]="'ENTITY_SELECTION_EMPTY_ERROR' | translate"></span>
                </div>
            </ng-template>

            <ng-template #elseBlock>
                <div *ngIf="entities?.length" class="es-container">
                    <div class="es-searchbar">
                        <input
                            type="text"
                            class="es-searchbar__input"
                            placeholder="Rechercher une structure..."
                            (input)="search($event)"
                        />
                    </div>
                    <div class="es-list-container">
                        <div class="es-list" *ngIf="filteredEntitiesAdh.length">
                            <h3 *ngIf="filteredEntitiesFrn.length || filteredEntitiesCollab.length">
                                {{ 'MEMBER_APP_LABEL' | translate }}
                            </h3>

                            <div
                                *ngFor="let entity of filteredEntitiesAdh; let i = index"
                                [ngClass]="{
                                    'es-list__selected': isSelected(entity),
                                    'es-list__item': !isSelected(entity)
                                }"
                                (click)="selectEntity(entity)"
                            >
                                <img class="es-list__icon" *ngIf="isSelected(entity)" src="assets/structure.svg" />
                                <div>{{ entity?.entityCode }} - {{ entity?.name }} - {{ entity?.role?.name }}</div>
                            </div>
                        </div>
                        <div class="es-list" *ngIf="filteredEntitiesFrn.length">
                            <h3 *ngIf="filteredEntitiesAdh.length || filteredEntitiesCollab.length">
                                {{ 'SUPPLIER_APP_LABEL' | translate }}
                            </h3>
                            <div
                                *ngFor="let entity of filteredEntitiesFrn; let i = index"
                                [ngClass]="{
                                    'es-list__selected': isSelected(entity),
                                    'es-list__item': !isSelected(entity)
                                }"
                                (click)="selectEntity(entity)"
                            >
                                <img class="es-list__icon" *ngIf="isSelected(entity)" src="assets/structure.svg" />
                                <div>{{ entity?.entityCode }} - {{ entity?.name }} - {{ entity?.role?.name }}</div>
                            </div>
                        </div>
                        <div class="es-list" *ngIf="filteredEntitiesCollab.length">
                            <h3 *ngIf="filteredEntitiesAdh.length || filteredEntitiesFrn.length">
                                {{ 'COLLAB_APP_LABEL' | translate }}
                            </h3>

                            <div
                                *ngFor="let entity of filteredEntitiesCollab; let i = index"
                                [ngClass]="{
                                    'es-list__selected': isSelected(entity),
                                    'es-list__item': !isSelected(entity)
                                }"
                                (click)="selectEntity(entity)"
                            >
                                <img class="es-list__icon" *ngIf="isSelected(entity)" src="assets/structure.svg" />
                                <div>{{ entity?.entityCode }} - {{ entity?.name }} - {{ entity?.role?.name }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="es-btn-container">
                        <button
                            [disabled]="!selectedEntity"
                            class="es-btn-validate"
                            [ngClass]="{ 'es-btn-validate--disabled': !selectedEntity }"
                            (click)="validate()"
                            [innerHTML]="'VALIDATE' | translate"
                        ></button>
                    </div>
                </div>
            </ng-template>
        </div>
    `,
    styleUrls: ['./entity-selector-page.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
    imports: [NgIf, NgFor, NgClass, TranslateModule],
})
export class EntitySelectorPageComponent implements OnInit, OnDestroy {
    sessionEntities$: Observable<IndexedSessionEntities>;

    @Input() title = 'SELECT_DEFAULT_ENTITY';

    entities: UserEntity[] = null;
    filteredEntitiesAdh: UserEntity[];
    filteredEntitiesFrn: UserEntity[];
    filteredEntitiesCollab: UserEntity[];

    selectedEntity: string;

    private destroyed$ = new Subject();

    constructor(private entityService: EntityService, private session: SessionService) {
        this.sessionEntities$ = this.session.sessionEntities$;
    }

    ngOnInit() {
        this.redirectToLoginPageIfNoAccessToken();
        this.session.fetchEntities(environment);

        this.sessionEntities$
            .pipe(
                filter((data) => !!data),
                map((obj) => Object.values(obj))
            )
            .subscribe((sessionEntitiesSnapshot) => {
                this.entities = [...sessionEntitiesSnapshot].sort(sortByProperty('name'));

                this.filteredEntitiesAdh = [...this.entities].filter((entity) =>
                    entity.id.includes(ENTITY_TYPE.member)
                );
                this.filteredEntitiesFrn = [...this.entities].filter(
                    (entity) => entity.id.includes(ENTITY_TYPE.supplier) || entity.id.includes(ENTITY_TYPE.vendor)
                );
                this.filteredEntitiesCollab = [...this.entities].filter((entity) =>
                    entity.id.includes(ENTITY_TYPE.collab)
                );
            });
    }

    search(searchedTerm) {
        this.filteredEntitiesAdh = this.entities.filter(
            (entity) => entity.id.includes(ENTITY_TYPE.member) && this.searchEntities(searchedTerm, entity)
        );

        this.filteredEntitiesFrn = this.entities.filter(
            (entity) =>
                (entity.id.includes(ENTITY_TYPE.supplier) || entity.id.includes(ENTITY_TYPE.vendor)) &&
                this.searchEntities(searchedTerm, entity)
        );

        this.filteredEntitiesCollab = this.entities.filter(
            (entity) => entity.id.includes(ENTITY_TYPE.collab) && this.searchEntities(searchedTerm, entity)
        );
    }

    selectEntity(entity) {
        this.selectedEntity = entity && entity.id;
    }

    isSelected(entity) {
        return entity && this.selectedEntity === entity.id;
    }

    validate() {
        if (this.selectedEntity.includes(ENTITY_TYPE.member)) {
            this.entityService.setContext(this.selectedEntity, APPLICATION_NAME.digicedra);
        } else if (
            this.selectedEntity.includes(ENTITY_TYPE.supplier) ||
            this.selectedEntity.includes(ENTITY_TYPE.vendor)
        ) {
            this.entityService.setContext(this.selectedEntity, APPLICATION_NAME.digicedrf);
        } else if (this.selectedEntity.includes(ENTITY_TYPE.collab)) {
            this.entityService.setContext(this.selectedEntity, APPLICATION_NAME.digierp);
        }
    }

    redirectToLoginPageIfNoAccessToken() {
        if (!Cookie.get('access_token')) {
            window.location.href = '/login';
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next(null);
        this.destroyed$.complete();
    }

    private searchEntities(searchedTerm, entity) {
        const term = (entity.entityCode + entity.name).toUpperCase();
        return term.includes(searchedTerm.target.value.toUpperCase());
    }
}
